import { Interval } from 'date-fns';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import { isValidDate } from '../../sharedComponents/dateHelper';
import { LabelWithTimezone } from '../../sharedComponents/LabelWithTimezone';
import { DayAndTimePicker } from '../../sharedComponents/DayAndTimePicker';
import { DatePickerType } from '../sidebar/TransportAssignmentConfirmationForm';
import { formatLocalizedDateTimeInTimeZone } from '../../sharedComponents/localizationHelper';
import { ValidationResult } from '../../sharedComponents/validationResult.type';

const dateTimeIntervalToString = (date: Interval, timeZone: string, locale: string): string => {
    const startString = isValidDate(date.start) ? formatLocalizedDateTimeInTimeZone(date.start, timeZone, locale) : '';
    const endString = isValidDate(date.end) ? formatLocalizedDateTimeInTimeZone(date.end, timeZone, locale) : '';

    if (startString === endString) {
        return startString;
    }

    return `${startString} - ${endString}`;
};

type TransportOrderBulkUpdateDatePickerFormGroupPropsBase = {
    displayFieldMessageId: string;
    displayFieldValueOverride?: string;
    inputFieldMessageId: string;
    inputFieldValue: Date | undefined;
    timeZone: string;
    onChange: (value: Date | undefined) => void;
    validation: ValidationResult;
    isFormDisabled: boolean;
    onParseError: (hasParseError: boolean) => void;
};

type TransportOrderBulkUpdateDatePickerFormGroupProps =
    | (TransportOrderBulkUpdateDatePickerFormGroupPropsBase & {
          datePickerType: DatePickerType.PICKUP_DATE_PICKER;
          displayFieldValue: Interval | undefined;
      })
    | (TransportOrderBulkUpdateDatePickerFormGroupPropsBase & {
          datePickerType: DatePickerType.DELIVERY_DATE_PICKER;
          displayFieldValue: Date | undefined;
      });

export const TransportOrderBulkUpdateDatePickerFormGroup = (
    props: TransportOrderBulkUpdateDatePickerFormGroupProps,
) => {
    const intl = useIntl();
    const displayFieldId = useId();

    const getDisplayValue = () => {
        if (props.displayFieldValueOverride !== undefined) {
            return props.displayFieldValueOverride;
        }

        if (props.displayFieldValue === undefined) {
            return '';
        }

        switch (props.datePickerType) {
            case DatePickerType.PICKUP_DATE_PICKER:
                return dateTimeIntervalToString(props.displayFieldValue, props.timeZone, intl.locale);
            case DatePickerType.DELIVERY_DATE_PICKER:
                return formatLocalizedDateTimeInTimeZone(props.displayFieldValue, props.timeZone, intl.locale);
        }
    };

    return (
        <div className="display-flex align-items-start">
            <div className="width-50pct">
                <LabelWithTimezone
                    htmlFor={displayFieldId}
                    messageId={props.displayFieldMessageId}
                    timeZoneData={{ timeZone: props.timeZone, date: props.displayFieldValue }}
                />
                <div className="input-group">
                    <input
                        id={displayFieldId}
                        className="form-control"
                        type="text"
                        placeholder={intl.formatMessage({ id: 'outboundOrderBook.common.noData' })}
                        disabled
                        value={getDisplayValue()}
                    />
                </div>
            </div>
            <div className="width-50pct margin-left-10 margin-bottom-20">
                <DayAndTimePicker
                    labelId={props.inputFieldMessageId}
                    timeZone={props.timeZone}
                    date={isValidDate(props.inputFieldValue) ? props.inputFieldValue : undefined}
                    validation={props.validation}
                    onDateChange={props.onChange}
                    isDisabled={props.isFormDisabled}
                    onParseError={(hasParseError) => props.onParseError(hasParseError)}
                />
            </div>
        </div>
    );
};
